import { call, put } from 'redux-saga/effects';
import { FlowjoCommons, UserServiceUrls } from '@portal/portal-core';

const { Net } = FlowjoCommons;

// Minimal shape of the user client config passed into every worker saga by the watcher.
export interface UserClientConfig {
  baseApiUrl?: string;
  getAuthToken?: () => string | null | undefined;
}

// Shape of a parsed `/api/user/v1/*` response envelope.
export interface JsonApiResponse {
  succeeded?: boolean;
  result?: Record<string, unknown>;
}

/**
 * Shared GET-and-dispatch pattern for the small user-context endpoints
 * (`/roles`, ...): builds the auth header, fetches
 * `UserServiceUrls.userBasePath()${path}`, and puts `onSuccess(parsed)` on a
 * 200 response.
 *
 * A non-200 response or a network/parse error is logged but does NOT dispatch
 * anything — the caller's reducer keeps whatever value it already had, rather
 * than clearing to empty on a transient failure. A caller that has never
 * successfully fetched yet is unaffected either way, since its reducer's
 * default state is already empty.
 */
export function* fetchAuthorizedJsonSaga<A extends { type: string }>(
  config: UserClientConfig,
  path: string,
  onSuccess: (parsed: JsonApiResponse) => A,
): Generator<unknown, void, unknown> {
  const url = `${config.baseApiUrl}${UserServiceUrls.userBasePath()}${path}`;
  const token = config.getAuthToken ? config.getAuthToken() : null;
  const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};

  try {
    const { code, data } = (yield call(Net.getJson, url, headers)) as {
      code: number;
      data: string;
    };

    if (code === Net.reponses.OK) {
      const parsed = JSON.parse(data) as JsonApiResponse;
      yield put(onSuccess(parsed));
    } else {
      // eslint-disable-next-line no-console
      console.error(`Failed to fetch ${path}: server responded with status ${code}`, data);
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(`Error fetching ${path}:`, err);
  }
}
